// The "Coval Accessible Data Palette". Eight fixed hues spaced in lightness and
// chroma (not hue alone) so they stay distinguishable under protanopia,
// deuteranopia, and tritanopia — the Okabe–Ito method.
//
// SCOPE — this is a DATA-VIZ + STATUS palette, and only those two things use it:
//   1. Chart series identity — the exports below.
//   2. Status / result semantics — pass/fail, success / danger / warning / info,
//      and lifecycle status like "Completed". Those tokens (theme.css) derive
//      their green / red-orange / amber / blue from these same hues.
// It is deliberately SEPARATE from the brand palette. Brand / UI chrome — buttons,
// nav, pipeline "stage" accents, marketing surfaces — must NOT use it and stays on
// brand tokens. The brand and data-viz palettes will be split into distinct token
// sets later; keep any new usage on the data-viz / status side of that line.
//
// The order is part of the palette: neighbouring entries are the most distinct,
// so a chart with N series takes the first N in order. Do not re-sort or
// reassign per chart — a given series index maps to the same color everywhere,
// which is what keeps identity consistent across chart types.
//
// Four hues — Amber, Sky blue, Magenta, Yellow — fall under WCAG 3:1 against a
// white surface at their `strong` tone. As filled areas, bars, or slices that is
// fine; a thin mark has no area to compensate with, so marks resolve through the
// theme-aware --coval-chart-mark-N tokens instead (each hue's `dark` tone in
// light, `mid` in dark — every slot clears 3:1 in both themes). Reach for
// chartMarkSeriesColors / lineSeriesColors for strokes and points, the bar-fill
// helpers for filled forms, and the fixed `strong` tones only where a concrete
// known hex is required (canvas painting, color-picker seeds).
//
// Eight is the categorical ceiling. For > 8 series, fold overflow into "Other",
// facet, or use a single hue; the helpers below cycle past 8 only as a fallback.
//
// Each hue is a four-tone ramp spanning the branded COLOR_PALETTE range:
//   - strong: the color-blind-safe identity value (the canonical series color).
//   - light:  pale fill / surface / hover tint (dark text sits on it).
//   - mid:    a softer second step for large areas or a secondary series.
//   - dark:   text on light tints, axis emphasis, and the dark-mode series color
//             (every dark tone clears 5:1 on white).
// light / mid / dark hold each hue and most of its chroma, moving only lightness.
//
// Canonical order — do not re-sort. Each entry has { name, light, mid, strong,
// dark }; `strong` is the categorical series value. The four hues marked
// "thin-mark" fall under WCAG 3:1 and need a label/outline as line strokes or
// small points (fine as fills). Types flow from the `as const` literal.
export const SERIES_RAMPS = [
  {
    name: "amber",
    light: "#f9ebcd",
    mid: "#f7cc6f",
    strong: "#e29b03",
    dark: "#724e00",
  }, // thin-mark
  {
    name: "blue",
    light: "#c2dff8",
    mid: "#4eaaf7",
    strong: "#015ba6",
    dark: "#002e54",
  },
  {
    name: "sky",
    light: "#def1fb",
    mid: "#a1daf9",
    strong: "#4abdfb",
    dark: "#006aa3",
  }, // thin-mark
  {
    name: "red-orange",
    light: "#f8cfc7",
    mid: "#f6775e",
    strong: "#bf2203",
    dark: "#611000",
  },
  {
    name: "magenta",
    light: "#f8e8f1",
    mid: "#f1bcd8",
    strong: "#e97fb8",
    dark: "#9f155f",
  }, // thin-mark
  {
    name: "yellow",
    light: "#f6f4d5",
    mid: "#efe787",
    strong: "#e9d919",
    dark: "#7a7107",
  }, // thin-mark
  {
    name: "green",
    light: "#c8f3e7",
    mid: "#61e6c2",
    strong: "#129a75",
    dark: "#06503c",
  },
  {
    // The achromatic slot (Okabe–Ito's black). `strong` is a mid-dark warm
    // neutral rather than a near-black: at #0f0c0a it was 1.2 ΔE from this
    // ramp's own `dark` tone — below a noticeable difference, leaving the ramp
    // with three usable tones — and byte-identical to the dark-mode canvas, so
    // an ink mark rendered invisible there. Same hue and saturation as `mid`,
    // stepped down in lightness, so it reads as ink rather than a borrowed
    // neutral: 9.8:1 on the light surface, 19.3 ΔE clear of `dark`.
    name: "ink",
    light: "#d6cdc7",
    mid: "#937765",
    strong: "#4f4036",
    dark: "#080605",
  },
] as const

// Categorical palette = the strong tone of each ramp, in canonical order.
const DATA_PALETTE = SERIES_RAMPS.map((ramp) => ramp.strong)

/**
 * The categorical series palette as a plain array — the `strong` identity tone
 * of each ramp in canonical order. For callers that seed a fixed palette (color
 * maps, swatch pickers) rather than asking for an N-length subset; for a chart
 * with a known series count, prefer chartSeriesColors(count).
 */
export const CHART_COLORS = DATA_PALETTE

// Look up a ramp by its strong tone, for rendering a series hue as a chip —
// pale `light` background under legible `dark` text (the strong tone can fall
// under WCAG contrast as text).
const RAMP_BY_STRONG = new Map(
  SERIES_RAMPS.map((ramp) => [ramp.strong.toLowerCase(), ramp]),
)

/**
 * The `{ light, mid, dark }` chip tones for a categorical `strong` series color —
 * background + legible text tones for the series hue as a label chip (table
 * cells, labels). Returns null for non-palette colors so callers can fall back.
 */
export function seriesChipTones(
  color: string,
): { light: string; mid: string; dark: string } | null {
  const ramp = RAMP_BY_STRONG.get(color.toLowerCase())
  return ramp ? { light: ramp.light, mid: ramp.mid, dark: ramp.dark } : null
}

/**
 * The full light → mid → strong → dark ramp for series `index`, cycling past the
 * eight-color ceiling. Use when a chart needs more than the identity color — a
 * light fill under a strong stroke, a dark label, a per-hue sequential scale.
 */
export function seriesRamp(index: number) {
  const n = SERIES_RAMPS.length
  return SERIES_RAMPS[((index % n) + n) % n]
}

/**
 * Single-series / primary-emphasis color — the palette's brand blue (6.9:1 on
 * white). Use when a chart shows one measure (sparkline, single-metric
 * distribution, a lone bar series) and wants a legible solid color.
 */
export const PRIMARY_SERIES_COLOR = "#015ba6"

/**
 * Theme-aware primary fill for single-series bar charts. Resolves via the
 * `--coval-chart-series-primary` CSS var to the data-viz blue's `mid` tone in
 * light mode (a softer fill) and its `strong` tone in dark (legible on the
 * near-black canvas). Prefer this over PRIMARY_SERIES_COLOR for bars; the fixed
 * strong blue stays for line/point marks that need a solid known value.
 */
export const PRIMARY_BAR_SERIES_COLOR = "var(--coval-chart-series-primary)"

// CSS-var reference for each canonical hue's theme-aware bar-fill tone (`mid` in
// light, `strong` in dark — defined as --coval-chart-series-N in theme.css).
// Same order as SERIES_RAMPS, so a series keeps its hue identity across chart
// types; only the tone softens for large filled areas.
const BAR_SERIES_VARS = SERIES_RAMPS.map(
  (_, i) => `var(--coval-chart-series-${i + 1})`,
)

const BAR_SERIES_VAR_BY_NAME = Object.fromEntries(
  SERIES_RAMPS.map((ramp, i) => [ramp.name, BAR_SERIES_VARS[i]]),
) as Record<(typeof SERIES_RAMPS)[number]["name"], string>

/**
 * Theme-aware bar-fill for a named hue (`mid` in light, `strong` in dark). Use
 * when a bar's color carries semantics (e.g. pass = green, fail = red-orange)
 * rather than positional series identity.
 */
export function barSeriesColor(
  name: (typeof SERIES_RAMPS)[number]["name"],
): string {
  return BAR_SERIES_VAR_BY_NAME[name]
}

/**
 * Theme-aware bar-fill colors for a chart with `count` series, in canonical
 * order. Like chartSeriesColors, but each resolves to the hue's `mid` tone in
 * light (softer for large filled areas) and `strong` in dark. Use for bar /
 * area fills; keep chartSeriesColors for line/point marks that need the fixed
 * strong identity tone.
 */
export function chartBarSeriesColors(count: number): string[] {
  return Array.from(
    { length: Math.max(count, 0) },
    (_, i) => BAR_SERIES_VARS[i % BAR_SERIES_VARS.length],
  )
}

function orderedSubset(count: number): string[] {
  return Array.from(
    { length: Math.max(count, 0) },
    (_, i) => DATA_PALETTE[i % DATA_PALETTE.length],
  )
}

/**
 * Ordered series colors for a chart with `count` series, taken from the front of
 * the canonical palette. Cycles past the eight-color ceiling (past the point
 * where color can carry identity — prefer folding to "Other" or faceting).
 */
export function chartSeriesColors(count: number): string[] {
  return orderedSubset(count)
}

// CSS-var reference for each canonical hue's theme-aware THIN-MARK tone (`dark`
// in light, `mid` in dark — defined as --coval-chart-mark-N in chart-tokens.css).
// A stroke or point has no area to compensate with, so it owes WCAG 1.4.11's 3:1
// against the canvas, and no single fixed tone clears that on both: `strong`
// misses on light for amber and sky, and on dark for blue and ink. Same order as
// SERIES_RAMPS, so a series keeps its hue identity across chart types.
const MARK_SERIES_VARS = SERIES_RAMPS.map(
  (_, i) => `var(--coval-chart-mark-${i + 1})`,
)

/**
 * Theme-aware mark colors for a chart with `count` series — line strokes, area
 * outlines, scatter points, legend dots. Prefer this over `chartSeriesColors`
 * anywhere the mark is thin; the fixed `strong` tones stay for callers that need
 * a concrete known hex (canvas painting, color-picker seeds).
 */
export function chartMarkSeriesColors(count: number): string[] {
  return Array.from(
    { length: Math.max(count, 0) },
    (_, i) => MARK_SERIES_VARS[i % MARK_SERIES_VARS.length],
  )
}

/**
 * Line-chart series colors — the thin-mark tones, in the same canonical order as
 * chartSeriesColors so a series keeps its hue across chart types. A 2px stroke in
 * a fill tone falls under 3:1 on the light canvas, so lines resolve through
 * --coval-chart-mark-N rather than a fixed tone.
 */
export function lineSeriesColors(count: number): string[] {
  return chartMarkSeriesColors(count)
}
